import {List} from '@dropbox/dig-components/list';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  ActivityLine,
  AiSearchLine,
  AnalyticsLine,
  CalendarLine,
  ChecklistLine,
  ChevronRightLine,
  CodeLine,
  GratitudeLine,
  LockLine,
  MetricsLine,
  NotificationLine,
  PersonMultipleLine,
  RotateLeftLine,
  SearchLine,
  SettingsLine,
  TableLine,
  WinLine,
  WorkbookLine,
  WorkstreamsLine,
} from '@dropbox/dig-icons/assets';
import {PulseUser, pulseUserAtom} from 'atoms/auth';
import {LabelGroup} from 'components/DSYS/LabelGroup';
import {Layout} from 'components/DSYS/Layout';
import {Link} from 'components/DSYS/Link';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {
  canManagePillarAdmin,
  canViewEditorDocumentAdmin,
  isFinanceAdmin,
  isSuperUser,
  isUserAdmin,
} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {ComponentProps, ReactNode} from 'react';
import {useNavigate} from 'react-router';

export const isAdminEnabled = (pulseUser?: PulseUser) => {
  return Boolean(pulseUser && (isUserAdmin(pulseUser) || isSuperUser(pulseUser)));
};

export const canAccessAdminPage = (pulseUser?: PulseUser) => {
  return isAdminEnabled(pulseUser) || isFinanceAdmin(pulseUser);
};

const AdminLink = ({
  to,
  label,
  description,
  icon,
}: {
  to: string;
  label: string;
  description: string;
  icon: ComponentProps<typeof UIIcon>['src'];
}) => (
  <Link to={to} hasNoUnderline monochromatic className={atoms({display: 'block'})}>
    <List.Item spacing="medium" gap="medium" className={atoms({borderRadius: 'Large'})}>
      <List.Accessory>
        <UIIcon src={icon} className={atoms({color: 'Text Subtle'})} />
      </List.Accessory>
      <List.Content>
        <LabelGroup
          withText={<Text isBold>{label}</Text>}
          withSubtext={
            <Text size="small" color="subtle">
              {description}
            </Text>
          }
        />
      </List.Content>
      <List.Accessory>
        <UIIcon src={ChevronRightLine} size="small" className={atoms({color: 'Text Subtle'})} />
      </List.Accessory>
    </List.Item>
  </Link>
);

const AdminCategory = ({title, children}: {title: string; children: ReactNode}) => (
  <Stack gap="8">
    <Title size={16}>{title}</Title>
    <List isSelectable spacing="medium" verticalAlign="center" padding="0" style={{gap: 2}}>
      {children}
    </List>
  </Stack>
);

const ContentSection = () => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const canManageKnowledgeLinks = isAdminEnabled(pulseUser);

  if (
    !isUserAdmin(pulseUser) &&
    !canViewEditorDocumentAdmin(pulseUser) &&
    !isSuperUser(pulseUser) &&
    !canManageKnowledgeLinks
  ) {
    return null;
  }

  return (
    <AdminCategory title="Content">
      {isUserAdmin(pulseUser) && (
        <>
          <AdminLink
            to={ROUTE_PATHS.ADMIN_CONFIDENTIAL_METRICS}
            label="Confidential metrics"
            description="Manage MNPI admins and confidential metric access."
            icon={LockLine}
          />
          <AdminLink
            to={ROUTE_PATHS.ADMIN_DELEGATES}
            label="Delegates"
            description="Help users by managing delegation on their behalf."
            icon={PersonMultipleLine}
          />
          <AdminLink
            to={ROUTE_PATHS.ADMIN_NOTIFICATIONS}
            label="Notifications"
            description="Create and manage scheduled notifications."
            icon={NotificationLine}
          />
        </>
      )}
      {canViewEditorDocumentAdmin(pulseUser) && (
        <AdminLink
          to={ROUTE_PATHS.ADMIN_DOCUMENTS}
          label="Documents"
          description="Manage planning and template documents."
          icon={ChecklistLine}
        />
      )}
      {canManagePillarAdmin(pulseUser) && (
        <>
          <AdminLink
            to={ROUTE_PATHS.ADMIN_PILLARS}
            label="Pillars"
            description="Manage the pillars used in planning."
            icon={WorkstreamsLine}
          />
          {isUserAdmin(pulseUser) && (
            <AdminLink
              to={ROUTE_PATHS.ADMIN_TIMEFRAMES}
              label="Timeframes"
              description="Add planning timeframes and choose who sees them."
              icon={CalendarLine}
            />
          )}
          {isUserAdmin(pulseUser) && (
            <AdminLink
              to={ROUTE_PATHS.ADMIN_TIMEFRAME_ASSIGNMENT}
              label="Timeframe assignment"
              description="Preview the timeframe each workstream gets from its narrative roots."
              icon={CalendarLine}
            />
          )}
          <AdminLink
            to={ROUTE_PATHS.ADMIN_ROADMAP_JIRA_GROUP_MAPPINGS}
            label="Jira group mappings"
            description="Map Roadmap Jira groups to Dropbox Teams."
            icon={PersonMultipleLine}
          />
        </>
      )}
      {isSuperUser(pulseUser) && (
        <>
          <AdminLink
            to={ROUTE_PATHS.ADMIN_MANAGE_BADGES}
            label="Badges"
            description="Create and manage badge definitions."
            icon={WinLine}
          />
          <AdminLink
            to={ROUTE_PATHS.ADMIN_CARD_MANAGEMENT}
            label="Gratitude cards"
            description="Manage gratitude card art, labels, and availability."
            icon={GratitudeLine}
          />
        </>
      )}
      {canManageKnowledgeLinks && (
        <AdminLink
          to={ROUTE_PATHS.ADMIN_KNOWLEDGE_LINKS}
          label="Knowledge links"
          description="Configure links for home knowledge chips."
          icon={WorkbookLine}
        />
      )}
    </AdminCategory>
  );
};

const WorkstreamsSection = () => {
  const pulseUser = useAtomValue(pulseUserAtom);

  if (!isUserAdmin(pulseUser) && !isSuperUser(pulseUser)) {
    return null;
  }

  return (
    <AdminCategory title="Workstreams">
      <AdminLink
        to={ROUTE_PATHS.ADMIN_WORKSTREAM_AUDIT_LOG}
        label="Workstream metrics audit"
        description="Review metric change history across workstreams."
        icon={MetricsLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_SCORECARD_METRIC_DIRECTIONS}
        label="Scorecard metric direction"
        description="Override whether higher or lower is better for scorecard metrics."
        icon={MetricsLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_WORKSTREAM_AUTO_ARCHIVE}
        label="Workstream auto-archive"
        description="Preview the workstreams eligible for quarterly auto-archive."
        icon={WorkstreamsLine}
      />
      {isSuperUser(pulseUser) && (
        <AdminLink
          to={ROUTE_PATHS.ADMIN_WORKSTREAM_AUTODRAFT}
          label="Workstream autodraft"
          description="Generate status-update drafts on demand for testing."
          icon={AiSearchLine}
        />
      )}
      <AdminLink
        to={ROUTE_PATHS.ADMIN_WORKSTREAM_DRAFT_POLICY}
        label="Workstream draft policy"
        description="Configure the active draft and planning timeframe policy."
        icon={ChecklistLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_WORKSTREAM_IMPORTER}
        label="Workstream importer"
        description="Import reviewed workstream rows from a planning Paper doc."
        icon={WorkstreamsLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_WORKSTREAM_ORPHANS}
        label="Orphaned workstreams"
        description="Find open workstreams whose DRIs have all left the company."
        icon={PersonMultipleLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_WORKSTREAM_READINESS}
        label="Workstream readiness"
        description="Review health scores for workstreams."
        icon={AnalyticsLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_WORKSTREAM_UPDATES}
        label="Workstream updates"
        description="Review every workstream's next update due date."
        icon={NotificationLine}
      />
    </AdminCategory>
  );
};

const WireframeSection = () => {
  const pulseUser = useAtomValue(pulseUserAtom);

  if (!isFinanceAdmin(pulseUser)) {
    return null;
  }

  return (
    <AdminCategory title="Wireframe">
      <AdminLink
        to={ROUTE_PATHS.ADMIN_WIREFRAME_DATA}
        label="Wireframe Data"
        description="Manage cost profiles and workstream headcount data."
        icon={TableLine}
      />
    </AdminCategory>
  );
};

const DeveloperToolsSection = () => {
  const pulseUser = useAtomValue(pulseUserAtom);

  if (!isSuperUser(pulseUser)) {
    return null;
  }

  return (
    <AdminCategory title="Developer tools">
      <AdminLink
        to={ROUTE_PATHS.ADMIN_APPS}
        label="API keys"
        description="Create and review internal app credentials."
        icon={SettingsLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_CELERY_TASKS}
        label="Tasks"
        description="Inspect background task status and failures."
        icon={ActivityLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_DCD_JOB_FAMILIES}
        label="DCD job families"
        description="Inspect and export the active DCD job-family catalog."
        icon={PersonMultipleLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_ASK_HEALTH}
        label="Ask health"
        description="Weekly answer quality, answer rate and speed on live Ask traffic."
        icon={ActivityLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_AMPLITUDE_LOOKUP}
        label="Amplitude user lookup"
        description="Map hashed analytics identifiers back to users."
        icon={SearchLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_AI_PROMPT}
        label="AI prompt"
        description="Test and tune platform AI prompt behavior."
        icon={AiSearchLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_CONTEXT_ENGINE}
        label="Context engine chat"
        description="Run admin-only context engine experiments."
        icon={AiSearchLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_OVERDUE_OUTCOMES}
        label="Overdue milestone action items"
        description="Preview or sync aggregate overdue-milestone action items."
        icon={NotificationLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_MIGRATIONS}
        label="Database migrations"
        description="Review Alembic heads, database revisions, and pending migrations."
        icon={CodeLine}
      />
      <AdminLink
        to={ROUTE_PATHS.ADMIN_RUNTIME}
        label="Runtime"
        description="Secrets cache, prompt cache, and diagnostics."
        icon={RotateLeftLine}
      />
    </AdminCategory>
  );
};

export const Admin = () => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const navigate = useNavigate();

  useDocumentTitle('Admin settings');

  if (!pulseUser) {
    return null;
  }

  if (!canAccessAdminPage(pulseUser)) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container breadcrumb={[{children: 'Admin', to: ROUTE_PATHS.ADMIN}]}>
      <Title size={24}>{t('admin_controls')}</Title>
      <Stack gap="44" className={atoms({paddingTop: '36'})}>
        <ContentSection />
        <WorkstreamsSection />
        <WireframeSection />
        <DeveloperToolsSection />
      </Stack>
    </Layout.Container>
  );
};
